/**
 * SectionSubNav — horizontal pill-style sub-navigation.
 *
 * Detects which section the current pathname belongs to (via findActiveSection)
 * and renders the legacy pages of that section as scrollable pills. Pills that
 * the active subscription tier doesn't unlock are dimmed and unclickable.
 *
 * Hidden entirely when the current route doesn't belong to any registered
 * section (e.g. onboarding, public pages, settings sub-pages with no match).
 */

import { useMemo } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { findActiveSection, SECTION_PAGES, SectionPage } from "@/lib/navigation/sections";
import { useSubscription } from "@/hooks/useSubscription";
import { useOrganization } from "@/contexts/OrganizationContext";
import { usePerformanceLab } from "@/contexts/PerformanceLabContext";
import { isFeatureEnabledForTier } from "@/lib/subscriptionTiers";
import { Lock } from "lucide-react";

function isPillActive(pathname: string, pillPath: string): boolean {
  const cleanPill = pillPath.split("?")[0];
  return pathname === cleanPill || pathname.startsWith(cleanPill + "/");
}

export function SectionSubNav() {
  const { pathname } = useLocation();
  const { effectiveTier } = useSubscription();
  const { currentRole } = useOrganization();

  const section = useMemo(() => findActiveSection(pathname), [pathname]);

  if (!section) return null;

  const pages = (SECTION_PAGES[section.key] ?? []).filter((page) => {
    // Hide pills explicitly marked as hideWhenLocked when their feature is disabled for the tier.
    if (page.hideWhenLocked && page.feature && !isFeatureEnabledForTier(effectiveTier, page.feature)) {
      return false;
    }
    if (!page.roles || page.roles.length === 0) return true;
    if (currentRole === "super_admin") return true;
    return currentRole ? page.roles.includes(currentRole) : false;
  });
  if (pages.length === 0) return null;

  return (
    <nav
      aria-label={`${section.label} pages`}
      className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-[57px] z-30"
    >
      <div className="overflow-x-auto scrollbar-none">
        <ul className="flex items-center gap-1 px-4 py-2 min-w-max">
          {pages.map((page) => (
            <SubNavPill
              key={page.path}
              page={page}
              active={isPillActive(pathname, page.path)}
              locked={
                page.feature
                  ? !isFeatureEnabledForTier(effectiveTier, page.feature)
                  : false
              }
            />
          ))}
        </ul>
      </div>
    </nav>
  );
}

function SubNavPill({
  page,
  active,
  locked,
}: {
  page: SectionPage;
  active: boolean;
  locked: boolean;
}) {
  const { openLab } = usePerformanceLab();
  const baseClasses =
    "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm tracking-tight transition-colors whitespace-nowrap";

  if (locked) {
    return (
      <li>
        <span
          aria-disabled="true"
          title="Not available on your current plan"
          className={cn(
            baseClasses,
            "font-medium text-muted-foreground/60 cursor-not-allowed select-none",
          )}
        >
          {page.label}
          <Lock className="h-3 w-3" aria-hidden="true" />
        </span>
      </li>
    );
  }

  if (page.action === "open_performance_lab") {
    return (
      <li>
        <button
          type="button"
          onClick={() => openLab()}
          className={cn(
            baseClasses,
            "font-medium text-muted-foreground hover:text-foreground hover:bg-muted",
          )}
        >
          {page.label}
        </button>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={page.path}
        className={cn(
          baseClasses,
          active
            ? "bg-primary text-primary-foreground font-semibold"
            : "font-medium text-muted-foreground hover:text-foreground hover:bg-muted",
        )}
      >
        {page.label}
      </NavLink>
    </li>
  );
}
